import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import UnifiedNavHeader from "../../../../components/navigation/UnifiedNavHeader";
import MobileSportsBar from "../../../../components/homePagePanel/MobileSportsBar";
import ErrorBoundary from "../../../../components/shared/error/ErrorBoundary";
import { getBrazilianStatus } from "../../../../utils/detectBrazil";
import { nflDashPtBR } from "../../../../components/mappings/sports-dash-mappings/nfl-dash.pt-br";
import { Header } from "../../../../components/dashboards/components/shared/Header";
import MobileGamesView from "../components/mobile/MobileGamesView";
import {
  getDefaultFilters,
  getGames,
  getTeams
} from "../services/nflDashboardService";
import { useQuickPrediction } from "../../../../components/dashboards/hooks/useQuickPrediction";
import RailNavigation from "../../../../components/dashboards/mobile/RailNavigation";
import { TeamSheet, MarketSheet } from "../../../../components/dashboards/mobile/FilterSheets";
import { DatePickerSheet } from "../../../../components/dashboards/components/shared/date-picker";
import WeekSheet from "../../../../components/dashboards/mobile/FilterSheets/WeekSheet";
import useNFLRailFilters from "../../../../components/dashboards/mobile/hooks/useNFLRailFilters";
import MobileNFLOddsSheet from "../components/mobile/MobileNFLOddsSheet";
import MobileNFLPredictionSheet from "../components/mobile/MobileNFLPredictionSheet";
import { NFL_HEADER_COPY } from "../constants/headerContent";
import { ensureISODate } from "../../../../components/dashboards/utils/dateUtils";

const DEFAULT_GAMES_PAGINATION = {
  currentPage: 1,
  perPage: 6,
  totalPages: 1
};

const NFLMobileDashboard = () => {
  const defaultFilters = useMemo(() => getDefaultFilters(), []);
  const defaultLeague = defaultFilters.league;
  const defaultSeason = defaultFilters.season;
  const defaultDate = defaultFilters.date;
  const defaultCountry = defaultFilters.country;
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gamesPagination, setGamesPagination] = useState(DEFAULT_GAMES_PAGINATION);
  const [railFiltersState, setRailFiltersState] = useState(null);
  const [showOddsSheet, setShowOddsSheet] = useState(false);
  const [selectedGameForOdds, setSelectedGameForOdds] = useState(null);
  const [showPredictionSheet, setShowPredictionSheet] = useState(false);
  const [selectedGameForPrediction, setSelectedGameForPrediction] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [predictionRequestId, setPredictionRequestId] = useState(0);
  const activePredictionRequestRef = useRef(null);
  const [predictionFlowActive, setPredictionFlowActive] = useState(false);
  const cacheCheckKeyRef = useRef(null);

  const {
    isLoading: isPredicting,
    usageState,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    getGamePrediction,
    isGameSelected,
    isGameFrozen,
    selectionCount,
    checkCachedPredictions
  } = useQuickPrediction();

  const isBrazilian = getBrazilianStatus();
  const t = useCallback(
    (text) => (isBrazilian ? nflDashPtBR[text] || text : text),
    [isBrazilian]
  );

  const handleRailFiltersChange = useCallback((nextFilters) => {
    setRailFiltersState(nextFilters);
    setGamesPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const {
    filters: railFilters,
    activeTab: railActiveTab,
    hasActiveFilters: railHasActiveFilters,
    primaryTabs: railPrimaryTabs,
    filterObjects: railFilterObjects,
    handleTabChange: handleRailTabChange,
    handleFilterClick,
    updateFilter: updateRailFilter,
    resetFilters: resetRailFilters,
    showDateSheet,
    setShowDateSheet,
    showTeamSheet,
    setShowTeamSheet,
    showWeekSheet,
    setShowWeekSheet,
    showStatusSheet,
    setShowStatusSheet
  } = useNFLRailFilters({
    initialDate: defaultFilters.date,
    initialTeam: null,
    onFiltersChange: handleRailFiltersChange
  });

  const handleGamesPageChange = useCallback((page) => {
    setGamesPagination((prev) => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, prev.totalPages))
    }));
  }, []);

  const handleOpenOdds = useCallback((game) => {
    setSelectedGameForOdds(game);
    setShowOddsSheet(true);
  }, []);

  const handleOpenPredictionSheet = useCallback(
    (game) => {
      if (!game) return;
      const gamePrediction = getGamePrediction(game.id);
      setSelectedGameForPrediction(game);
      setSelectedPrediction(gamePrediction);
      setShowPredictionSheet(true);
    },
    [getGamePrediction]
  );

  const handleClosePredictionSheet = useCallback(() => {
    setShowPredictionSheet(false);
    setSelectedGameForPrediction(null);
    setSelectedPrediction(null);
  }, []);

  const handlePredictRequest = useCallback(async () => {
    // Track each explicit user request so we only auto-close after a real submission starts/finishes
    setPredictionRequestId((prev) => {
      const next = prev + 1;
      activePredictionRequestRef.current = next;
      return next;
    });
    setPredictionFlowActive(true);
    await predictSelectedGames("nfl");
    setPredictionFlowActive(false);
  }, [predictSelectedGames]);

  const handleCloseOdds = useCallback(() => {
    setShowOddsSheet(false);
    setSelectedGameForOdds(null);
  }, []);

  useEffect(() => {
    if (showPredictionSheet && selectedGameForPrediction) {
      const updatedPrediction = getGamePrediction(selectedGameForPrediction.id);
      setSelectedPrediction(updatedPrediction);
    }
  }, [showPredictionSheet, selectedGameForPrediction, getGamePrediction]);

  useEffect(() => {
    // Mark that a prediction cycle actually started
    if (isPredicting) {
      return;
    }

    const shouldFinalize =
      !isPredicting &&
      !predictionFlowActive &&
      activePredictionRequestRef.current === predictionRequestId &&
      predictionRequestId > 0;

    if (shouldFinalize) {
      clearSelections();
      handleClosePredictionSheet();
      activePredictionRequestRef.current = null;
    }
  }, [
    clearSelections,
    handleClosePredictionSheet,
    isPredicting,
    predictionFlowActive,
    predictionRequestId
  ]);

  const loadGamesData = useCallback(async () => {
    const railState = railFiltersState ?? {};
    const requestFilters = {
      league: defaultLeague,
      season: railState.seasonYear ? String(railState.seasonYear) : defaultSeason,
      date: railState.week ? "" : ensureISODate(railState.date, defaultDate),
      country: defaultCountry,
      teamId: railState.team?.id ?? null,
      weekId: railState.week || "",
      status: railState.status || "all"
    };

    try {
      setLoading(true);
      setError(null);
      let gamesData = await getGames(requestFilters);
      gamesData = gamesData.filter((data) => !data?.teams?.away?.name || !data?.teams?.away?.home)

      const gamesWithIds = gamesData.map((game, index) => ({
        ...game,
        id: game.game?.id || game.id || `game-${index}`
      }));
      setGames(gamesWithIds);
      setGamesPagination((prev) => {
        const totalPages = Math.max(1, Math.ceil(gamesWithIds.length / prev.perPage));
        return { ...prev, totalPages, currentPage: Math.min(prev.currentPage, totalPages) };
      });
    } catch (err) {
      console.error("Failed to load mobile NFL games:", err);
      setError(t("Failed to load games"));
    } finally {
      setLoading(false);
    }
  }, [defaultCountry, defaultDate, defaultLeague, defaultSeason, railFiltersState, t]);

  const loadTeamsData = useCallback(
    async ({ silent = false } = {}) => {
      try {
        if (!silent) {
          setLoading(true);
          setError(null);
        }
        const teamsData = await getTeams({
          league: defaultLeague,
          season: defaultSeason,
          conference: ""
        });
        setTeams(teamsData);
      } catch (err) {
        console.error("Failed to load mobile NFL teams:", err);
        if (!silent) {
          setError(t("Failed to load teams"));
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [defaultLeague, defaultSeason, t]
  );

  useEffect(() => {
    loadTeamsData({ silent: true });
  }, [loadTeamsData]);

  useEffect(() => {
    loadGamesData();
  }, [loadGamesData]);

  // Prime predictions from cache/Firestore when games change
  useEffect(() => {
    if (!games.length) return;
    const gameIds = games.map((g) => g.id).filter(Boolean);
    if (!gameIds.length) return;
    const key = gameIds.slice().sort().join(",");
    if (cacheCheckKeyRef.current === key) return;
    cacheCheckKeyRef.current = key;
    checkCachedPredictions?.("nfl", gameIds).catch((err) =>
      console.error("checkCachedPredictions failed (NFL mobile):", err)
    );
  }, [games, checkCachedPredictions]);

  const refreshData = useCallback(() => {
    loadGamesData();
  }, [loadGamesData]);

  return (
    <ErrorBoundary onReset={refreshData}>
      <div className="min-h-screen bg-[#050311] text-white">
        <UnifiedNavHeader title="NFL Dashboard" showBackButton />

        {/* Mobile Sports Navigation Bar */}
        <MobileSportsBar />

        <div className="px-4 py-6 pb-24">
          <Header t={t} {...NFL_HEADER_COPY} />

          <div className="mt-4 mb-4">
            <RailNavigation
              sport="nfl"
              primaryTabs={railPrimaryTabs}
              filters={railFilterObjects}
              activeTab={railActiveTab}
              onTabChange={handleRailTabChange}
              onFilterChange={handleFilterClick}
              onReset={resetRailFilters}
              hasActiveFilters={railHasActiveFilters}
              className="border-y border-white/10 bg-[#050311]/95 backdrop-blur"
            />
          </div>

          {error && (
            <div className="flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-100">
              <AlertCircle className="text-red-300" size={18} />
              <p>{error}</p>
            </div>
          )}

          <MobileGamesView
            games={games}
            loading={loading}
            pagination={gamesPagination}
            onPageChange={handleGamesPageChange}
            t={t}
            isGameSelected={isGameSelected}
            isGameFrozen={isGameFrozen}
            onToggleSelection={toggleGameSelection}
            getGamePrediction={getGamePrediction}
            onViewOdds={handleOpenOdds}
            onViewPrediction={handleOpenPredictionSheet}
        />
        </div>

        <DatePickerSheet
          isOpen={showDateSheet}
          onClose={() => setShowDateSheet(false)}
          selectedDate={ensureISODate(railFilters.date || defaultDate)}
          onDateSelect={(dateValue) => {
            const formatted = ensureISODate(dateValue, defaultDate);
            updateRailFilter("date", formatted);
            updateRailFilter("week", null);
            setShowDateSheet(false);
          }}
        />

        <TeamSheet
          isOpen={showTeamSheet}
          onClose={() => setShowTeamSheet(false)}
          selectedTeam={railFilters.team}
          onTeamSelect={(team) => {
            updateRailFilter("team", team);
            setShowTeamSheet(false);
          }}
          teams={teams}
          sport="nfl"
        />

        <WeekSheet
          isOpen={showWeekSheet}
          onClose={() => setShowWeekSheet(false)}
          currentWeek={railFilters.week?.weekId || railFilters.week}
          onWeekChange={(weekData) => {
            updateRailFilter("week", weekData.weekId);
            updateRailFilter("seasonType", weekData.seasonType);
            updateRailFilter("date", null);
            setShowWeekSheet(false);
          }}
          seasonYear={
            railFilters.seasonYear ||
            (defaultSeason ? Number(defaultSeason) : new Date().getFullYear())
          }
        />

        <MarketSheet
          isOpen={showStatusSheet}
          onClose={() => setShowStatusSheet(false)}
          currentMarket={railFilters.status || "all"}
          onMarketChange={(statusValue) => {
            updateRailFilter("status", statusValue);
            setShowStatusSheet(false);
          }}
          sport="nfl"
        />

        <MobileNFLOddsSheet
          isOpen={showOddsSheet}
          game={selectedGameForOdds}
          onClose={handleCloseOdds}
        />

        <MobileNFLPredictionSheet
          isOpen={showPredictionSheet}
          game={selectedGameForPrediction}
          prediction={selectedPrediction}
          selectionCount={selectionCount}
          usageState={usageState}
          isLoading={isPredicting}
          onPredict={handlePredictRequest}
          onClear={clearSelections}
          onClose={handleClosePredictionSheet}
          t={t}
        />
      </div>
    </ErrorBoundary>
  );
};

export default NFLMobileDashboard;
